import { Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Membership = {
  id: string;
  activity_id: string;
  activity_code?: string;
  activity_name?: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  member_role: string;
  status: string;
  joined_on: string;
  session_name?: string;
};

export default function ActivityMembershipsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentFilter = searchParams.get('student_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>('active');
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-activity-memberships', studentFilter, sessionId, status, q],
    queryFn: async () => {
      const res = await api.get<Membership[]>('/education/activity-memberships', {
        params: {
          student_id: studentFilter,
          session_id: sessionId,
          status,
          q: q || undefined,
        },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const endMembership = useMutation({
    mutationFn: (id: string) => api.post(`/education/activity-memberships/${id}/end`),
    onSuccess: () => {
      message.success(t('activityMembershipEnded'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['education-activities'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('activityMemberships')}
      description={t('activityMembershipsIntro')}
      extra={
        <Space wrap>
          {canManage ? (
            <Button
              type="primary"
              onClick={() =>
                navigate(
                  studentFilter
                    ? `/education/activities/memberships/new?student_id=${studentFilter}`
                    : '/education/activities/memberships/new'
                )
              }
            >
              {t('enrollActivityMember')}
            </Button>
          ) : null}
          {canManage ? (
            <Button onClick={() => navigate('/education/activities/memberships/bulk')}>
              {t('bulkActivityEnrollment')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/activities')}>{t('backToActivities')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('session')}>
              <EducationSessionSelect
                style={{ minWidth: 180 }}
                value={sessionId}
                onChange={(v) => setSessionId(v || undefined)}
                allowClear
              />
            </Form.Item>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'active', label: t('active') },
                  { value: 'waitlisted', label: t('waitlisted') },
                  { value: 'ended', label: t('ended') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
            {studentFilter ? (
              <Button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('student_id');
                  setSearchParams(next);
                }}
              >
                {t('clearStudentFilter')}
              </Button>
            ) : null}
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('activity'),
                render: (_: unknown, row: Membership) => `${row.activity_code || ''} — ${row.activity_name || ''}`,
              },
              {
                title: t('student'),
                render: (_: unknown, row: Membership) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              { title: t('memberRole'), dataIndex: 'member_role', width: 100 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => (
                  <Tag color={v === 'active' ? 'success' : v === 'waitlisted' ? 'warning' : undefined}>{v}</Tag>
                ),
              },
              { title: t('joinedOn'), dataIndex: 'joined_on', width: 120 },
              {
                title: t('actions'),
                width: 180,
                render: (_: unknown, row: Membership) => (
                  <Space size="small">
                    <Button size="small" onClick={() => navigate(`/education/activities/${row.activity_id}/members`)}>
                      {t('open')}
                    </Button>
                    {canManage && (row.status === 'active' || row.status === 'waitlisted') ? (
                      <Popconfirm
                        title={t('activityMembershipEndConfirm')}
                        onConfirm={() => endMembership.mutate(row.id)}
                      >
                        <Button size="small" loading={endMembership.isPending}>
                          {t('endMembership')}
                        </Button>
                      </Popconfirm>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
